import i18n from 'i18next';
import { createSlice } from '@reduxjs/toolkit';
import { Cart, DiscountType } from '@types';
import { CURRENCY_SIGNS } from '@config/constants';

import {
  getCartData, updateProductQty, removePromotionFromCart, addPromotionToCart, removeCartItem,
} from '@store/slices/cart/actions';

import {
  onCartPageLoad, onApplyPromoError, onApplyPromoSuccess, onRemoveBtnClick, onPageView,
  waitForAdobeAnalytics,
} from '@utils';
import {
  convertOrderItemsToProducts, convertOrderSummary, convertPromoCode, convertDiscounts,
} from './converters';

const initialState: Cart = {
  isLoading: true,
  cartId: null,
  products: [],
  removedProducts: [],
  marketRestrictedProducts: [],
  mergedProducts: [],
  currency: CURRENCY_SIGNS.USD,
  orderInfo: { productTotal: 0, orderTotal: 0 },
  isPromoLoading: false,
  promoError: null,
  promoCode: null,
  discounts: [],
  isFreeTrial: false,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    updateQuantity: (state, { payload }) => {
      const productIndex = state.products.findIndex((p) => p.uid === payload.uid);
      if (productIndex > -1) {
        state.products[productIndex].qty = payload.qty;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCartData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCartData.fulfilled, (state, { payload: { wGetCart, wGetCartDiscounts, mergedProducts } }) => {
        const { couponTotalDiscount, orderItemDiscounts } = wGetCartDiscounts.discounts;

        const isFreeTrial = orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
        const products = convertOrderItemsToProducts(wGetCart.cart.hiConsistsOf);
        const summary = convertOrderSummary(wGetCart.cart.pricPrice, products);
        const promoCode = convertPromoCode(wGetCart.cart.corPromoCodes, wGetCart.cart.pricPrice, couponTotalDiscount);
        const discounts = convertDiscounts(orderItemDiscounts, promoCode, wGetCart.cart.pricPrice);

        state.isLoading = false;
        state.cartId = wGetCart.cart.uid;
        state.products = products;
        state.currency = summary.currency;
        state.orderInfo = summary.orderInfo;
        state.removedProducts = wGetCart.removedProducts;
        state.marketRestrictedProducts = wGetCart.marketRestrictedProducts;
        state.mergedProducts = mergedProducts;
        state.promoCode = promoCode;
        state.discounts = discounts;
        state.isFreeTrial = isFreeTrial;

        // Fire analytics asynchronously without blocking state update
        waitForAdobeAnalytics().then(() => {
          if (products.length > 0) {
            onCartPageLoad(products, summary.currency);
          }
          onPageView();
        }).catch(() => {
          // Silently fail if Adobe Analytics doesn't load
        });
      })
      .addCase(getCartData.rejected, (state) => {
        state.isLoading = false;
        onPageView();
      })
      .addCase(removeCartItem.pending, (state, { meta }) => {
        const productIndex = state.products.findIndex((p) => p.uid === meta.arg);
        if (productIndex > -1) {
          state.products[productIndex].updating = true;
          state.products[productIndex].updateError = false;
        }
      })
      .addCase(removeCartItem.fulfilled, (state, { payload: { wGetCart, wGetCartDiscounts }, meta }) => {
        const { couponTotalDiscount, orderItemDiscounts } = wGetCartDiscounts.discounts;

        const isFreeTrial = orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
        const products = convertOrderItemsToProducts(wGetCart.cart.hiConsistsOf);
        const summary = convertOrderSummary(wGetCart.cart.pricPrice, products);
        const promoCode = convertPromoCode(wGetCart.cart.corPromoCodes, wGetCart.cart.pricPrice, couponTotalDiscount);
        const discounts = convertDiscounts(orderItemDiscounts, promoCode, wGetCart.cart.pricPrice);

        const deletedProduct = state.products.find((item) => item.uid === meta.arg);
        if (deletedProduct) {
          onRemoveBtnClick(deletedProduct, state.currency);
        }

        state.products = products;
        state.currency = summary.currency;
        state.orderInfo = summary.orderInfo;
        state.promoCode = promoCode;
        state.discounts = discounts;
        state.isFreeTrial = isFreeTrial;
      })
      .addCase(removeCartItem.rejected, (state, { meta }) => {
        const productIndex = state.products.findIndex((p) => p.uid === meta.arg);
        if (productIndex > -1) {
          state.products[productIndex].updating = false;
          state.products[productIndex].updateError = true;
        }
      })
      .addCase(updateProductQty.fulfilled, (state, { payload: { wGetCart, wGetCartDiscounts } }) => {
        const { couponTotalDiscount, orderItemDiscounts } = wGetCartDiscounts.discounts;

        const isFreeTrial = orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
        const products = convertOrderItemsToProducts(wGetCart.cart.hiConsistsOf);
        const summary = convertOrderSummary(wGetCart.cart.pricPrice, products);
        const promoCode = convertPromoCode(wGetCart.cart.corPromoCodes, wGetCart.cart.pricPrice, couponTotalDiscount);
        const discounts = convertDiscounts(orderItemDiscounts, promoCode, wGetCart.cart.pricPrice);

        state.isLoading = false;
        state.cartId = wGetCart.cart.uid;
        state.products = products;
        state.currency = summary.currency;
        state.orderInfo = summary.orderInfo;
        state.removedProducts = wGetCart.removedProducts;
        state.marketRestrictedProducts = wGetCart.marketRestrictedProducts;
        state.promoCode = promoCode;
        state.discounts = discounts;
        state.isFreeTrial = isFreeTrial;
      })
      .addCase(addPromotionToCart.pending, (state) => {
        state.isPromoLoading = true;
      })
      .addCase(addPromotionToCart.fulfilled, (state, { payload }) => {
        state.isPromoLoading = false;

        const { couponTotalDiscount, orderItemDiscounts } = payload.wGetCartDiscounts.discounts;
        const isFreeTrial = orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
        const products = convertOrderItemsToProducts(payload.wSetPromoCodes.hiConsistsOf);
        const summary = convertOrderSummary(payload.wSetPromoCodes.pricPrice, products);
        const promoCode = convertPromoCode(
          payload.wSetPromoCodes.corPromoCodes,
          payload.wSetPromoCodes.pricPrice,
          couponTotalDiscount,
        );
        const discounts = convertDiscounts(orderItemDiscounts, promoCode, payload.wSetPromoCodes.pricPrice);

        state.products = products;
        state.currency = summary.currency;
        state.orderInfo = summary.orderInfo;
        state.promoCode = promoCode;
        state.promoError = null;
        state.discounts = discounts;
        state.isFreeTrial = isFreeTrial;
        onApplyPromoSuccess(promoCode?.value);
      })
      .addCase(addPromotionToCart.rejected, (state, { payload }) => {
        state.isPromoLoading = false;
        state.promoError = i18n.t('cart.summary.promoCode.noDiscountsError', { payload });
        onApplyPromoError(payload as string);
      })
      .addCase(removePromotionFromCart.pending, (state) => {
        state.isPromoLoading = true;
      })
      .addCase(removePromotionFromCart.fulfilled, (state, { payload }) => {
        state.isPromoLoading = false;

        const { couponTotalDiscount, orderItemDiscounts } = payload.wGetCartDiscounts.discounts;
        const isFreeTrial = orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
        const products = convertOrderItemsToProducts(payload.wSetPromoCodes.hiConsistsOf);
        const summary = convertOrderSummary(payload.wSetPromoCodes.pricPrice, products);
        const promoCode = convertPromoCode(
          payload.wSetPromoCodes.corPromoCodes,
          payload.wSetPromoCodes.pricPrice,
          couponTotalDiscount,
        );
        const discounts = convertDiscounts(orderItemDiscounts, promoCode, payload.wSetPromoCodes.pricPrice);

        state.products = products;
        state.currency = summary.currency;
        state.orderInfo = summary.orderInfo;
        state.promoCode = promoCode;
        state.discounts = discounts;
        state.isFreeTrial = isFreeTrial;
      })
      .addCase(removePromotionFromCart.rejected, (state) => {
        state.isPromoLoading = false;
      });
  },
});

export const {
  updateQuantity,
} = cartSlice.actions;

export const cartReducer = cartSlice.reducer;
